import { action, computed, observable, runInAction, makeObservable } from 'mobx';
import { ContactProps } from '../types/types';
import { get, patch, post } from '@partsbadger/utils';
import { AddressProps } from '@partsbadger/library';

class ContactStore {
    loading = false;
    contact?: ContactProps;

    addresses: Array<AddressProps> = [];
    requirements_list = [];
    feedback_questions: [] | undefined;
    default_shipping_address?: AddressProps;
    default_billing_address?: AddressProps;
    default_requirements?: Array<{
        id: number;
        name: string;
        description: string;
        type: string;
        is_input: boolean;
        field_type: string;
    }>;

    constructor() {
        makeObservable(this, {
            // Observables
            loading: observable,
            contact: observable,
            addresses: observable,
            requirements_list: observable,
            default_shipping_address: observable,
            default_billing_address: observable,
            default_requirements: observable,
            feedback_questions: observable,
            // Actions
            setLoading: action,
            getContactById: action,
            saveAddress: action,
            patchAddress: action,
            getAdditionalRequirements: action,
            getAdditionalRequirementsAndDocuments: action,
            updateInvoiceInformation: action,
            sendFeedback: action,

            // Computed
            billingAddresses: computed,
            shippingAddresses: computed,
        });
    }

    setLoading(loading: boolean) {
        runInAction(() => {
            this.loading = loading;
        });
    }

    async getContactById(contact_id: number) {
        await get(`/customer/contacts/${contact_id}/`).then(data => {
            runInAction(() => {
                this.contact = data;
                this.default_shipping_address = data.default_addresses.shipping_address;
                this.default_billing_address = data.default_addresses.billing_address;

                this.addresses = data.addresses;
            });
        });
    }

    saveAddress(contact_id: number, payload = {}) {
        return post(`/customer/contacts/${contact_id}/addresses/`, payload);
    }

    patchAddress(contact_id: number, address_id?: number, payload = {}) {
        return patch(`/customer/contacts/${contact_id}/addresses/${address_id}/`, payload);
    }

    getAdditionalRequirements = () => {
        get(`/customer/additional-requirements/`).then(data => {
            runInAction(() => {
                this.requirements_list = data.results;
            });
        });
    };

    getAdditionalRequirementsAndDocuments = async (contact_id: number) => {
        await get(`/customer/contacts/${contact_id}/additional-requirements-documents/`).then(data => {
            runInAction(() => {
                this.default_requirements = data.requirements
                    ? data.requirements.map((r: any) => {
                          return {
                              id: r.id,
                              name: r.requirement.name,
                              description: r.requirement.description,
                              type: 'string',
                              is_input: false,
                              field_type: 'string',
                          };
                      })
                    : [];
            });
        });
    };

    get billingAddresses() {
        return this.addresses.filter(address => address.type === 'Billing' || address.type === 'Billing and Shipping');
    }

    get shippingAddresses() {
        return this.addresses.filter(address => address.type === 'Shipping' || address.type === 'Billing and Shipping');
    }

    async updateInvoiceInformation(contact_id: number, payload: { invoice_email_address: string }) {
        await post(`/customer/contacts/${contact_id}/modify-email/`, payload);
    }

    sendFeedback = async (payload: any) => {
        if (payload.isSalesOrder) {
            await patch(`/customer/sales-orders/send-feedback-sales-order/`, { customer_feedback: payload })
                .then(data => {})
                .catch(err => {});
        } else {
            await post(`/customer/contacts/send-feedback/`, payload)
                .then(data => {})
                .catch(err => {});
        }
    };

    getFeedbackQuestions() {
        return get(`/customer/sales-orders/feedback-questions-sales-order/`).then(
            resp => (this.feedback_questions = resp)
        );
    }
}

export default new ContactStore();
